// import dependencies
import React, { FunctionComponent, useMemo, useEffect, useState } from 'react'

import { Checkbox, CommonButton, ConvertStringToReact, ToggleButton, Tooltip, TrustArcLogo } from '@common/components'
import { TimePeriod, COUPON_DISCOUNT_TYPE, PRODUCT_SKU, BillingPeriodKey, BillingPeriod } from '@common/constants'
import { getSessionState, saveSessionState, connectRedux, GoToComponentProps, formatDate } from '@common/utils'

import { ConnectedLmiProFormatCurrency } from '@lmipro/connectedComponents'
import { LMIPRO_USER_TYPES, ADDONS } from '@lmipro/constants'
import {
    mapLmiProOrderSummaryState,
    mapLmiProOrderSummaryActions,
    LmiProOrderSummaryActionsType,
} from '@lmipro/connectedComponents/OrderSummary/connector'
import { ConnectedModal } from '@common/connectedComponents/Modal'
import { LmiProOrderSummaryState } from '@lmipro/connectedComponents/OrderSummary/state'
import { calculateTotalPriceWithAddons, getCouponTotalSavings, getAddonPrice } from '@lmipro/utils'
import { LmiProMemoOrderSummaryAddon } from '@lmipro/components'
import { getAddonProrateAmount } from '@lmipro/utils/calculate-prorated-amount'

import { isEmpty } from 'lodash-es'
import toggleStyles from '@common/components/ToggleButton/toggle-button.module.css'
import paymentStyles from '../PaymentFormInfo/payment-info.module.css'
import styles from './order-summary.module.css'

type LmiProOrderSummaryProps = GoToComponentProps<
    LmiProOrderSummaryState,
    LmiProOrderSummaryActionsType,
    {
        hidePrice?: boolean
        isTabletOrMobileView?: boolean
        isMobileView?: boolean
    }
>

type addonsContentType = {
    year: string
    month: string
    months: string
    percentageOffPromoDiscount: string
    percentageOffVolumeDiscount: string
    prorateDiscount: string
    totalVolumeDiscount: string
    tooltips: {
        [key: string]: string
    }
}

type toggleContentType = {
    label1: string
    label2: string
    subCallout?: string | undefined
}

const LmiProOrderSummary: FunctionComponent<LmiProOrderSummaryProps> = ({ state, actions, props }) => {
    const {
        billingFrequency,
        selectedPlan,
        selectedAddons,
        content,
        CurrencyCode,
        currentVolumeDiscountPrice,
        baseTierAnnualPrice,
        baseTierMonthlyPrice,
        baseTierMonthlyAnnualPrice,
        currentTierPrice,
        calculatedCredit,
        calculatedProratedAmount,
        couponProcessedDataPerSku,
        isCouponValidAndApplied,
        couponDiscountType,
        couponDoubleDipAmountForNewUser,
        agentsAdded,
        endpointsAdded,
        extraAddonsSelected,
        currentTier,
        addonsVolumeDiscountTotal,
        disablePlanSelection,
        productPrices,
        totalAgents,
        endpoints: totalEndpoints,
        couponCode,
        language,
    } = state
    // coupon discounts
    const planCouponData = couponProcessedDataPerSku ? couponProcessedDataPerSku[selectedPlan.sKU] : null
    const isCouponAppliedtoPlan = isCouponValidAndApplied && !isEmpty(planCouponData)
    const planCouponDiscountPercentage = planCouponData?.discountPercentage || 0
    const planCouponDiscountAmount = planCouponData?.discountTotalSavings || 0
    const isPercentageCoupon = couponDiscountType === COUPON_DISCOUNT_TYPE.PERCENTAGEOFF
    const totalCouponDiscountSavings = getCouponTotalSavings(
        isCouponValidAndApplied,
        couponProcessedDataPerSku,
        couponDoubleDipAmountForNewUser
    )
    const isRepriceCouponAndApplied = couponDiscountType === COUPON_DISCOUNT_TYPE.REPRICE && isCouponAppliedtoPlan
    const isAnnual = billingFrequency === TimePeriod.Year

    const [isMonthlyAnnualPlan, setIsMonthlyAnnualPlan] = useState(false)
    const [isAnnualBilledMonthlyPlan, setIsAnnualBilledMonthlyPlan] = useState(false)
    const [toggleContent, setToggleContent] = useState<toggleContentType>({ label1: '', label2: '', subCallout: '' })
    const [isAnnualContract, setAnnualContract] = useState(false)

    useEffect(() => {
        if (selectedPlan.billingPeriods.find((item) => item.key === BillingPeriodKey.ANNUALCONTRACT)) {
            setAnnualContract(true)
        }
    }, [selectedPlan.billingPeriods])

    const baseTierPrice = isAnnualContract ? baseTierMonthlyAnnualPrice : baseTierMonthlyPrice
    const [isAnnualOnly, setIsAnnualOnly] = useState(false)

    // const isAnnualBilledMonthlyPresent = () =>
    //     !isEmpty(state.selectedPlan?.billingPeriods?.filter((item) => item.key === BillingPeriodKey.ANNUALCONTRACT))

    // WAE1-3734: Annual Contract Billed Monthly Conditional rendering :- calculatedBasePrice
    // tier 1 (base tier) price to calculate discounts off
    const calculatedBasePriceKey = isAnnualBilledMonthlyPlan ? baseTierMonthlyAnnualPrice : baseTierPrice
    let calculatedBasePrice = isAnnual ? baseTierPrice * 12 : calculatedBasePriceKey
    if (
        selectedPlan.sKU === PRODUCT_SKU.LMIPRO_REMOTE_SUPPORT_NAMED_V2 ||
        selectedPlan.sKU === PRODUCT_SKU.LMIPRO_REMOTE_ACCESS
    ) {
        calculatedBasePrice = isAnnual
            ? baseTierPrice * 12
            : calculatedBasePriceKey * (agentsAdded?.quantity ? agentsAdded?.quantity + 1 : 1)
    }
    // only used in UI display
    const basePriceTotal = calculatedBasePrice

    // coupon processed current Tier price
    const couponProcessedMonthlyCurrentTierPrice = isRepriceCouponAndApplied ? baseTierPrice : currentTierPrice
    const couponProcessedMonthlyAnnualCurrentTierPrice = isRepriceCouponAndApplied
        ? baseTierMonthlyAnnualPrice
        : currentTierPrice
    const couponProcessedAnnualCurrentTierPrice = isRepriceCouponAndApplied ? baseTierAnnualPrice : currentTierPrice
    // current selected tier to calculate total off
    const calculatedMonthlyPrice = couponProcessedMonthlyCurrentTierPrice
    const calculatedMonthlyAnnualPrice = couponProcessedMonthlyAnnualCurrentTierPrice
    const calculatedAnnualPrice = couponProcessedAnnualCurrentTierPrice * 12
    const calculatedTotalPriceForPlanKey = isAnnualBilledMonthlyPlan
        ? calculatedMonthlyAnnualPrice
        : calculatedMonthlyPrice
    let calculatedTotalPriceForPlan = isAnnual ? calculatedAnnualPrice : calculatedTotalPriceForPlanKey

    if (
        selectedPlan.sKU === PRODUCT_SKU.LMIPRO_REMOTE_SUPPORT_NAMED_V2 ||
        selectedPlan.sKU === PRODUCT_SKU.LMIPRO_REMOTE_ACCESS
    ) {
        calculatedTotalPriceForPlan *= agentsAdded?.quantity ? agentsAdded?.quantity + 1 : 1
    }

    // const baseAnnualAmount = baseTierPrice * 12

    // annual discount off the selected tier
    const annualBasePriceForDiscount = isAnnualContract ? baseTierMonthlyAnnualPrice : baseTierPrice
    let annualDiscountAmount = isMonthlyAnnualPlan ? (annualBasePriceForDiscount - baseTierAnnualPrice) * 12 : 0
    if (
        selectedPlan.sKU === PRODUCT_SKU.LMIPRO_REMOTE_SUPPORT_NAMED_V2 ||
        selectedPlan.sKU === PRODUCT_SKU.LMIPRO_REMOTE_ACCESS
    ) {
        annualDiscountAmount *= agentsAdded?.quantity ? agentsAdded?.quantity + 1 : 1
    }

    // total of extra add-ons
    const totalExtraAddOns = extraAddonsSelected.reduce(
        (previousSum, addon) =>
            previousSum + getAddonPrice(addon, isAnnual, CurrencyCode, currentTier) * addon.quantity,
        0
    )

    // total price of plan plus addons (volume discount is already accounted in the plan)
    const currentTotalPrice = useMemo(
        () =>
            calculateTotalPriceWithAddons(
                disablePlanSelection ? 0 : calculatedTotalPriceForPlan,
                selectedAddons,
                selectedPlan,
                isAnnual,
                CurrencyCode,
                currentTier
            ),
        [
            calculatedTotalPriceForPlan,
            isAnnual,
            selectedAddons,
            selectedPlan,
            CurrencyCode,
            currentTier,
            disablePlanSelection,
        ]
    )

    const calculatedProrateAmount = calculatedProratedAmount < 0 ? Math.abs(calculatedProratedAmount) : 0

    // total savings (add the prorated discount only if its negative, otherwise its not a saving anymore)
    const totalDiscountSavings =
        (disablePlanSelection
            ? calculatedProrateAmount
            : (isAnnual ? annualDiscountAmount : 0) +
              currentVolumeDiscountPrice +
              calculatedProrateAmount +
              totalCouponDiscountSavings) +
        addonsVolumeDiscountTotal +
        calculatedCredit

    const subtotalWithCouponDiscounts = currentTotalPrice - totalCouponDiscountSavings - calculatedCredit
    const subtotal = state.calculatedNetTotal ? state.calculatedNetTotal : subtotalWithCouponDiscounts
    const total = state.calculatedTotal ? state.calculatedTotal : subtotal

    // labels and state display
    const isAddonFlow = state.isUserLoggedIn && state.userType === LMIPRO_USER_TYPES.ADDON
    const displayProrated =
        isAddonFlow && calculatedProratedAmount !== 0 && state.anniversaryDate && state.anniversaryDate.length > 1

    const addonsContent: addonsContentType = {
        year: content.year,
        month: content.month,
        months: content.months,
        percentageOffPromoDiscount: content.percentageOffPromoDiscount,
        percentageOffVolumeDiscount: content.percentageOffVolumeDiscount,
        prorateDiscount: content.proratedAdjustment,
        totalVolumeDiscount: content.totalVolumeDiscount,
        tooltips: {
            [ADDONS.AGENT]: content.agentsVolumediscountTooltip,
            [ADDONS.ENDPOINT]: content.endpointsVolumediscountTooltip,
            [ADDONS.MOBILE_ADDON]: content.mobileVolumediscountTooltip,
            [ADDONS.CAMERA_SHARE_ADDON]: content.cameraShareVolumediscountTooltip,
            [ADDONS.ENDPOINT_PROTECTION_ADDON]: content.endpointProtectionVolumediscountTooltip,
            proratedadjustmentTooltip: content.proratedadjustmentTooltip,
            totalVolumeDiscountTooltip: content.totalVolumeDiscountTooltip,
        },
    }

    const anniversaryDate = displayProrated ? formatDate(new Date(state.anniversaryDate), 'UTC', state.Locale) : ''
    const proratedTooltip = `${content.billingCycleCredit} ${anniversaryDate} - ${content.today}`
    const discountLineClasses = `${styles.small} ${styles.teal}`

    const getSubTotal = () => {
        if (isAnnualOnly)
            // Only "Annual" is selected in sitecore
            return baseTierAnnualPrice * 12
        if (isAnnual)
            // One of the options is "Annual"
            return (isAnnualContract ? baseTierMonthlyAnnualPrice : baseTierPrice) * 12
        if (isAnnualBilledMonthlyPlan)
            // "Annual" and "Annual billed monthly" selected in Sitecore
            return basePriceTotal

        // One of the options is "Monthly"
        return baseTierPrice
    }

    const getSubTotalText = () => {
        if (isAnnual) return isAnnualContract ? baseTierMonthlyAnnualPrice : baseTierPrice
        if (isAnnualBilledMonthlyPlan) return baseTierMonthlyAnnualPrice
        return baseTierPrice
    }

    useEffect(() => {
        const qualtricsData = getSessionState('qualtrics_data_gh')
        const purchaseTermKey = isAnnualBilledMonthlyPlan ? BillingPeriod.ANNUALBILLEDMONTHLY : BillingPeriod.MONTHLY
        saveSessionState('qualtrics_data_gh', {
            ...(!qualtricsData ? {} : qualtricsData),
            // id: '', // added on GTM side
            sku: selectedPlan.sKU,
            // WAE1-3734: Annual Contract Billed Monthly Conditional rendering
            purchase_term: isAnnual ? BillingPeriod.ANNUAL : purchaseTermKey,
            purchase_type: isAddonFlow ? 'addon' : 'new',
            order_total: calculatedTotalPriceForPlan,
            // referral_channel: '', // added on GTM side
            promo_code_applied: couponCode ?? '',
            site_language: language,
        })
    }, [
        selectedPlan.sKU,
        isAnnual,
        isAnnualBilledMonthlyPlan,
        isAddonFlow,
        calculatedTotalPriceForPlan,
        couponCode,
        language,
    ])

    useEffect(() => {
        if (selectedPlan.billingPeriods.length > 1) {
            setIsMonthlyAnnualPlan(true)
            setToggleContent({
                label1:
                    selectedPlan.billingPeriods.find(
                        (item) => item.key === BillingPeriodKey.ANNUALCONTRACT || item.key === BillingPeriodKey.MONTHLY
                    )?.name || '',
                label2: content.annual,
                ...(isAnnualContract && {
                    subCallout: isAnnual
                        ? content.annuallySubText.replace(
                              '{Percentage}',
                              (
                                  Number(
                                      (((baseTierPrice - baseTierAnnualPrice) * 12) / (baseTierPrice * 12)).toFixed(2)
                                  ) * 100
                              ).toString()
                          )
                        : content.monthlySubText,
                }),
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        content.annual,
        selectedPlan.billingPeriods,
        selectedPlan.billingPeriods.length,
        selectedPlan.sKU,
        isAnnualContract,
    ])

    useEffect(() => {
        if (state.selectedPlan) {
            const isAnnualBillingPresent = !isEmpty(
                state.selectedPlan?.billingPeriods?.filter((item) => item.key === BillingPeriodKey.ANNUAL)
            )
            setIsAnnualOnly(state.selectedPlan?.billingPeriods?.length === 1 && isAnnualBillingPresent)
        }
    }, [state.selectedPlan])

    const renderMonthlyNote = () => {
        const isAnnualMonthlyContract = selectedPlan.billingPeriods.find(
            (item) => item.key === BillingPeriodKey.ANNUALCONTRACT
        )

        if (isAnnualMonthlyContract) {
            return isAnnual ? content.annualPlanNote : content.annualConractPlanNote
        }
        return isAnnual ? content.annualPlanNote : content.monthlyPlanNote
    }

    return (
        <div>
            <div className={styles.wrapper}>
                <h2 className={isMonthlyAnnualPlan ? styles.header : styles['header-without-toggle']}>
                    {content.orderSummary}
                </h2>
                {isMonthlyAnnualPlan && (
                    <ToggleButton
                        content={toggleContent}
                        onChangeFn={() => {
                            if (
                                selectedPlan.billingPeriods.find((item) => item.key === BillingPeriodKey.ANNUALCONTRACT)
                            ) {
                                setIsAnnualBilledMonthlyPlan(true)
                                actions.setBillingFrequency(isAnnual ? TimePeriod.AnnualBilledMonthly : TimePeriod.Year)
                            } else {
                                actions.setBillingFrequency(isAnnual ? TimePeriod.Month : TimePeriod.Year)
                            }
                            if (!isAnnual) {
                                setIsAnnualBilledMonthlyPlan(false)
                                toggleContent.subCallout = toggleContent.subCallout
                                    ? content.annuallySubText.replace(
                                          '{Percentage}',
                                          (
                                              Number(
                                                  (
                                                      ((baseTierPrice - baseTierAnnualPrice) * 12) /
                                                      (baseTierPrice * 12)
                                                  ).toFixed(2)
                                              ) * 100
                                          ).toString()
                                      )
                                    : ''
                            } else {
                                toggleContent.subCallout = toggleContent.subCallout ? content.monthlySubText : ''
                            }
                        }}
                        disabled={isAddonFlow}
                        isOn={isAnnual}
                        modifier={`${toggleStyles['wrapper--order-summary']} ${
                            toggleContent.subCallout ? toggleStyles['wrapper--annual-contract'] : ''
                        }`}
                        id={`toggle-${Math.random()}`}
                        isLarge
                        theme="lmipro"
                        monthlyTooltip={toggleContent.subCallout ? content.monthlyTooltip : ''}
                    />
                )}
                {!disablePlanSelection && (
                    <div className={styles.section}>
                        <div className={styles.row} data-qat="plan-summary">
                            <span className={styles['item-title']} data-qat="line-item-name">
                                {state.selectedPlan.name}
                            </span>
                            <span className={styles.item} data-qat="line-item-subtotal">
                                <ConnectedLmiProFormatCurrency value={getSubTotal()} />
                            </span>
                        </div>
                        {selectedPlan?.shortDescription && (
                            <div className={styles.row}>
                                <span className={styles.small}>{`${selectedPlan.shortDescription}`}</span>
                            </div>
                        )}
                        {isMonthlyAnnualPlan && (
                            <div className={styles.row}>
                                <span className={styles.small}>
                                    {/* WAE1-3734: Annual Contract Billed Monthly Conditional rendering */}
                                    <ConnectedLmiProFormatCurrency value={getSubTotalText()} />
                                    &nbsp;x&nbsp;
                                    <span className={styles['item-sub-line']}>
                                        {isAnnual ? 12 : 1} {content.months}
                                    </span>
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {agentsAdded &&
                    selectedPlan.sKU !== PRODUCT_SKU.LMIPRO_REMOTE_SUPPORT_NAMED_V2 &&
                    selectedPlan.sKU !== PRODUCT_SKU.LMIPRO_REMOTE_ACCESS && (
                        <div className={styles.section}>
                            <LmiProMemoOrderSummaryAddon
                                addon={agentsAdded}
                                subLabels={[
                                    `${ADDONS.ADDON_PRICE_REPLACE} x ${agentsAdded.quantity} ${content.additional} ${agentsAdded.title}`,
                                ]}
                                isAnnual={isAnnual}
                                currency={CurrencyCode}
                                content={addonsContent}
                                couponProcessedDataPerSku={couponProcessedDataPerSku}
                                isCouponValidAndApplied={isCouponValidAndApplied}
                                isPercentageCoupon={isPercentageCoupon}
                                currentTier={currentTier}
                                quantity={agentsAdded.quantity}
                                title={`${content.additional} ${agentsAdded.title}`}
                                prorateDiscount={getAddonProrateAmount(productPrices, agentsAdded)}
                            />
                        </div>
                    )}

                {endpointsAdded && (
                    <div key="endpointsAdded" className={styles.section}>
                        <LmiProMemoOrderSummaryAddon
                            addon={endpointsAdded}
                            subLabels={[
                                `${ADDONS.ENDPOINTS_ADDONS_INTERVAL * endpointsAdded.quantity} ${content.additional} ${
                                    endpointsAdded.quantityLabelPlural
                                } ${content.added}`,
                                `${ADDONS.ADDON_PRICE_REPLACE} / ${content.per} ${ADDONS.ENDPOINTS_ADDONS_INTERVAL} ${content.countAbbr}`,
                            ]}
                            isAnnual={isAnnual}
                            currency={CurrencyCode}
                            content={addonsContent}
                            couponProcessedDataPerSku={couponProcessedDataPerSku}
                            isCouponValidAndApplied={isCouponValidAndApplied}
                            isPercentageCoupon={isPercentageCoupon}
                            currentTier={currentTier}
                            quantity={endpointsAdded.quantity}
                            title={`${content.additional} ${endpointsAdded.title}`}
                            prorateDiscount={getAddonProrateAmount(productPrices, endpointsAdded)}
                        />
                    </div>
                )}

                {extraAddonsSelected.length >= 1 && (
                    <div key="extraAddonsSelected" className={styles.section}>
                        <div className={styles.row} data-qat="total-extra-add-ons">
                            <span className={styles['item-title']} data-qat="line-item-name">
                                {content.addOns || 'Add-Ons'}
                            </span>
                            <span className={`${styles.item}`} data-qat="line-item-subtotal">
                                <ConnectedLmiProFormatCurrency value={totalExtraAddOns} />
                            </span>
                        </div>
                        {extraAddonsSelected
                            .filter((a) => a.quantity)
                            .map((addon, index) => (
                                <LmiProMemoOrderSummaryAddon
                                    key={`extraAddonsSelected${index}`}
                                    addon={addon}
                                    showSubtotal={false}
                                    subLabels={[
                                        addon.sKU.includes(ADDONS.ENDPOINT)
                                            ? `${ADDONS.ADDON_PRICE_REPLACE} x ${totalEndpoints} ${
                                                  content.devices || 'Devices'
                                              }`
                                            : `${ADDONS.ADDON_PRICE_REPLACE} x ${totalAgents} ${
                                                  state.agentContent.quantityLabel || 'Agents'
                                              }`,
                                    ]}
                                    isAnnual={isAnnual}
                                    currency={CurrencyCode}
                                    content={addonsContent}
                                    couponProcessedDataPerSku={couponProcessedDataPerSku}
                                    isCouponValidAndApplied={isCouponValidAndApplied}
                                    isPercentageCoupon={isPercentageCoupon}
                                    currentTier={currentTier}
                                    title={addon.quantityLabelPlural}
                                    quantity={addon.quantity}
                                    prorateDiscount={getAddonProrateAmount(productPrices, addon)}
                                />
                            ))}
                    </div>
                )}

                {totalDiscountSavings > 0 && (
                    <div className={styles.section}>
                        <div className={styles.row} data-qat="discount-summary">
                            <span className={styles['item-title']} data-qat="line-item-name">
                                {content.discount}
                            </span>
                            <span className={styles.item} data-qat="line-item-subtotal">
                                -<ConnectedLmiProFormatCurrency value={totalDiscountSavings} />
                            </span>
                        </div>
                        {isAnnual && annualDiscountAmount > 0 && (
                            <div className={styles['sub-section']} data-qat="annual-discount">
                                <div className={styles.row}>
                                    <span className={discountLineClasses} data-qat="annual-savings-label">
                                        {content.percentageOffAnnualDiscount}
                                    </span>
                                    <span className={discountLineClasses} data-qat="annual-discount-price">
                                        -<ConnectedLmiProFormatCurrency value={annualDiscountAmount} />
                                    </span>
                                </div>
                            </div>
                        )}
                        {
                            /* Use reduce to combine all volume discounts to an array with single object */
                            extraAddonsSelected
                                .concat(agentsAdded)
                                .concat(endpointsAdded)
                                .reduce(
                                    (prev, curr) => [
                                        {
                                            discountPrice: prev[0].discountPrice + (curr?.discountPrice ?? 0),
                                            discountPercentage:
                                                prev[0].discountPercentage + (curr?.discountPercentage ?? 0),
                                        },
                                    ],
                                    [
                                        {
                                            discountPrice:
                                                currentVolumeDiscountPrice > 0 ? currentVolumeDiscountPrice : 0,
                                            discountPercentage: 0,
                                        },
                                    ]
                                )
                                .map((addon) =>
                                    addon.discountPrice ? (
                                        <div
                                            key={addon.discountPrice}
                                            className={styles['sub-section']}
                                            data-qat="volume-discount"
                                        >
                                            <div className={styles.row}>
                                                <span className={discountLineClasses}>
                                                    {addonsContent.totalVolumeDiscount}
                                                    <Tooltip
                                                        content={addonsContent.tooltips.totalVolumeDiscountTooltip}
                                                        additionalClass={styles['order-tooltip']}
                                                        contentClass={styles['order-tooltip-content']}
                                                    />
                                                </span>
                                                <span className={discountLineClasses} data-qat="volume-discount-price">
                                                    -<ConnectedLmiProFormatCurrency value={addon.discountPrice} />
                                                </span>
                                            </div>
                                        </div>
                                    ) : null
                                )
                        }
                        {isCouponAppliedtoPlan && planCouponDiscountAmount && (
                            <div className={styles['sub-section']} data-qat="coupon-discount">
                                <div className={styles.row}>
                                    <span className={discountLineClasses}>
                                        <span data-qat="coupon-discount-percentage">
                                            {isPercentageCoupon ? (
                                                `${planCouponDiscountPercentage}%`
                                            ) : (
                                                <ConnectedLmiProFormatCurrency value={planCouponDiscountAmount} />
                                            )}
                                            &nbsp;
                                        </span>
                                        {content.percentageOffPromoDiscount}
                                    </span>
                                    <span className={discountLineClasses} data-qat="coupon-discount-price">
                                        -
                                        <ConnectedLmiProFormatCurrency value={planCouponDiscountAmount} />
                                    </span>
                                </div>
                            </div>
                        )}
                        {couponDoubleDipAmountForNewUser > 0 && (
                            <div className={styles['sub-section']} data-qat="coupon-weird-discount">
                                <div className={styles.row}>
                                    <span className={discountLineClasses}>
                                        {content.couponAdjustmentSavings}
                                        <Tooltip
                                            content={content.couponAdjustmentSavingsPopupText}
                                            additionalClass={styles['order-tooltip']}
                                            contentClass={styles['order-tooltip-content']}
                                        />
                                    </span>
                                    <span className={discountLineClasses} data-qat="coupon-weird-discount-price">
                                        -
                                        <ConnectedLmiProFormatCurrency value={couponDoubleDipAmountForNewUser} />
                                    </span>
                                </div>
                            </div>
                        )}
                        {displayProrated && (
                            <div className={styles['sub-section']}>
                                <div className={styles.row}>
                                    <span className={discountLineClasses} data-qat="prorated_label">
                                        {content.proratedAdjustment}
                                        <Tooltip
                                            content={proratedTooltip}
                                            additionalClass={styles['order-tooltip']}
                                            contentClass={styles['order-tooltip-content']}
                                        />
                                    </span>
                                    <span className={discountLineClasses} data-qat="prorated_amount">
                                        <ConnectedLmiProFormatCurrency value={calculatedProratedAmount} />
                                    </span>
                                </div>
                            </div>
                        )}
                        {calculatedCredit > 0 && (
                            <div className={styles['sub-section']}>
                                <div className={styles.row}>
                                    <span className={discountLineClasses} data-qat="credit_label">
                                        {content.upgradeSubscriptionCredit}
                                        <Tooltip
                                            content={content.upgradeSubscriptionCreditTooltip}
                                            additionalClass={styles['order-tooltip']}
                                            contentClass={styles['order-tooltip-content']}
                                        />
                                    </span>
                                    <span className={discountLineClasses} data-qat="credit_amount">
                                        <ConnectedLmiProFormatCurrency value={-calculatedCredit} />
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className={`${styles.section} ${styles.total}`} data-qat="totals-summary">
                    <div className={`${styles.row}  ${styles['row--subtotal']}`} data-qat="order-subtotal">
                        <span data-qat="label">{content.subtotal}</span>
                        <span data-qat="amount">
                            {state.totalLoading ? '--' : <ConnectedLmiProFormatCurrency value={subtotal} />}
                        </span>
                    </div>
                    <div className={`${styles.row}  ${styles['row--subtotal']}`} data-qat="order-estimated-tax">
                        <span data-qat="label">
                            {content.estimatedTax}{' '}
                            <Tooltip
                                content={content.estimatedTaxPopupInfo}
                                additionalClass={styles['order-tooltip']}
                                contentClass={styles['order-tooltip-content']}
                            />
                        </span>

                        <span data-qat="amount">
                            {state.totalLoading ? (
                                '--'
                            ) : (
                                <>
                                    {state.calculatedTax ? (
                                        <ConnectedLmiProFormatCurrency value={state.calculatedTax} />
                                    ) : (
                                        content.tBD
                                    )}
                                </>
                            )}
                        </span>
                    </div>
                    <div className={`${styles.row} ${styles['row--total']}`} data-qat="order-total">
                        <span data-qat="label">
                            {toggleContent.subCallout && !isAnnual
                                ? content.annualContractTotal
                                : `${content.total} ${CurrencyCode}`}
                        </span>
                        <span data-qat="amount">
                            {state.totalLoading ? (
                                `${content.calculating}...`
                            ) : (
                                <ConnectedLmiProFormatCurrency value={total > 0 ? total : 0} />
                            )}
                        </span>
                    </div>
                    <div className={`${styles.row} ${styles['row--total-note']}`} data-qat="subscription-applied-days">
                        <span>{renderMonthlyNote()}</span>
                    </div>
                    {calculatedCredit > 0 && (
                        <div
                            className={`${styles.row} ${styles['row--total-note']}`}
                            data-qat="subscription-applied-days"
                        >
                            <span>
                                {content.upgradeSubscriptionCreditNote &&
                                    content.upgradeSubscriptionCreditNote.replace(
                                        '{upgradeRemainingDays}',
                                        state.subscriptionInfo.remainingDays.toString()
                                    )}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            <div className={`${paymentStyles.terms}`}>
                <ConvertStringToReact htmlString={content.pricingNote} />{' '}
                <button
                    className={paymentStyles['optout-button']}
                    onClick={() => actions.setDisplayOptOut(!state.displayOptOut)}
                    type="button"
                >
                    {content.optOut}
                </button>
                {state.displayOptOut && (
                    <Checkbox
                        label={content.optOutCheckboxLabel}
                        modifier={paymentStyles['optout-checkbox']}
                        checked={state.isUserOptOut}
                        onchange={() => actions.setIsUserOptOut(!state.isUserOptOut)}
                        qaTag="checkout-form-optout"
                    />
                )}
            </div>
            {!props.isTabletOrMobileView && <TrustArcLogo label={content.trustArcSupport} />}
            <ConnectedModal isOpen={state.modal.isModalOpen} closeable onClose={() => actions.closeErrorModal()}>
                <div data-qat="negative-total-restriction-modal">
                    <h3>{state.content.upgradeCreditErrorMessage}</h3>
                    <div>
                        <ConvertStringToReact htmlString={state.content.upgradeCreditErrorAssistance} />
                    </div>
                    <CommonButton onClick={() => actions.closeErrorModal()} theme="lmipro">
                        {state.content.upgradeClose}
                    </CommonButton>
                </div>
            </ConnectedModal>
        </div>
    )
}

export const ConnectedLmiProOrderSummary = connectRedux(
    LmiProOrderSummary,
    mapLmiProOrderSummaryState,
    mapLmiProOrderSummaryActions
)

export default ConnectedLmiProOrderSummary
